import { useState, useEffect } from "react";
import * as Dialog from '@radix-ui/react-dialog';
import { RefreshCw, CheckCircle2, AlertCircle, Play, Eye, X, ExternalLink } from 'lucide-react';
import { SocialIcon, PLATFORM_COLORS as BRAND_COLORS } from './SocialIcon';

interface GeneratedContent {
  _id: string;
  articleId: string;
  platform: string;
  contentText: string;
  hook?: string;
  cta?: string;
  videoScript?: string;
  scheduledAt?: string;
  status: "generated" | "scheduled" | "posted" | "failed";
  createdAt: string;
  updatedAt: string;
  article?: { title: string; slug: string; content?: string; blogPostSlug?: string };
}

interface ArticleWithContent {
  _id: string;
  title: string;
  slug: string;
  blogPostSlug?: string;
  imageUrl?: string;
  content: { [platform: string]: GeneratedContent };
  platforms: string[];
  totalContent: number;
  readyToPost: number;
  posted: number;
  failed: number;
}

type PlatformType = "twitter" | "linkedin" | "instagram" | "facebook" | "tiktok" | "reddit" | "youtube_shorts" | "email" | "push";

const ALL_PLATFORMS: PlatformType[] = ["twitter", "linkedin", "instagram", "facebook", "tiktok", "reddit", "youtube_shorts", "email", "push"];

const PLATFORM_NAMES: Record<string, string> = {
  twitter: 'Twitter/X', linkedin: 'LinkedIn', instagram: 'Instagram', facebook: 'Facebook',
  tiktok: 'TikTok', youtube_shorts: 'YouTube', reddit: 'Reddit', email: 'Email', push: 'Push',
};
const PLATFORM_COLORS = BRAND_COLORS;

const CARD = "bg-white/[0.04] border border-white/[0.07] rounded-2xl backdrop-blur-sm";
const INPUT = "bg-white/[0.05] border border-white/[0.1] rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-primary/50 transition-all";

function ContentViewer() {
  const [articles, setArticles] = useState<ArticleWithContent[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedContent, setSelectedContent] = useState<GeneratedContent | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [platformFilter, setPlatformFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedPlatforms, setSelectedPlatforms] = useState<{ [articleId: string]: string[] }>({});
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => { fetchContent(); }, [platformFilter, statusFilter]);

  const fetchContent = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (platformFilter !== "all") params.append("platform", platformFilter);
      if (statusFilter !== "all") params.append("status", statusFilter);
      const token = localStorage.getItem("adminToken");
      const response = await fetch(`/api/content?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      if (response.ok) {
        const data = await response.json();
        const contentItems: GeneratedContent[] = data.content || [];
        const articleMap = new Map<string, ArticleWithContent>();

        contentItems.forEach((item) => {
          const articleId = typeof item.articleId === 'object'
            ? (item.articleId as { _id: string })._id || String(item.articleId)
            : String(item.articleId);

          if (!articleMap.has(articleId)) {
            let imageUrl: string | undefined;
            if (item.article?.content) {
              const imgMatch = item.article.content.match(/<img[^>]+src="([^">]+)"/i);
              imageUrl = imgMatch ? imgMatch[1] : undefined;
            }
            articleMap.set(articleId, {
              _id: articleId, title: item.article?.title || "Unknown Article",
              slug: item.article?.slug || "", imageUrl,
              blogPostSlug: item.article?.blogPostSlug,
              content: {}, platforms: [], totalContent: 0, readyToPost: 0, posted: 0, failed: 0,
            });
          }

          const article = articleMap.get(articleId)!;
          if (!article.content[item.platform]) {
            article.content[item.platform] = item;
            article.platforms.push(item.platform);
            article.totalContent++;
            if (item.status === "generated") article.readyToPost++;
            if (item.status === "posted") article.posted++;
            if (item.status === "failed") article.failed++;
          }
        });

        setArticles(Array.from(articleMap.values()));
      }
    } catch (err) {
      console.error("Failed to fetch content:", err);
      setError("Failed to load content");
    } finally {
      setLoading(false);
    }
  };

  const handlePostNow = async (contentId: string) => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await fetch(`/api/content/${contentId}/post`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) { setSuccess("Content posted successfully!"); fetchContent(); }
      else { setError("Failed to post content"); }
    } catch (err) { console.error(err); setError("Failed to post content"); }
  };

  const handlePostSelected = async (articleId: string) => {
    const selected = selectedPlatforms[articleId] || [];
    if (selected.length === 0) { setError("No platforms selected for posting"); return; }
    const article = articles.find(a => a._id === articleId);
    if (!confirm(`Post to ${selected.length} platforms for article: "${article?.title}"?`)) return;

    const token = localStorage.getItem("adminToken");
    let successCount = 0, errorCount = 0;
    for (const platform of selected) {
      const content = article?.content[platform];
      if (content && content.status === "generated") {
        try {
          const r = await fetch(`/api/content/${content._id}/post`, {
            method: "POST", headers: { Authorization: `Bearer ${token}` },
          });
          if (r.ok) successCount++; else errorCount++;
        } catch { errorCount++; }
      }
    }
    setSuccess(`Posted to ${successCount} platforms${errorCount > 0 ? ` (${errorCount} failed)` : ''}`);
    setSelectedPlatforms(prev => ({ ...prev, [articleId]: [] }));
    fetchContent();
  };

  const handlePlatformSelect = (articleId: string, platform: string, checked: boolean) => {
    setSelectedPlatforms(prev => {
      const current = prev[articleId] || [];
      return { ...prev, [articleId]: checked ? [...current, platform] : current.filter(p => p !== platform) };
    });
  };

  const getStatusCls = (status: string) => {
    if (status === "posted") return "bg-green/15 text-green";
    if (status === "failed") return "bg-red/15 text-red";
    if (status === "scheduled") return "bg-orange/15 text-orange";
    return "bg-primary/15 text-primary";
  };

  const stats = {
    total: articles.reduce((s, a) => s + a.totalContent, 0),
    posted: articles.reduce((s, a) => s + a.posted, 0),
    scheduled: articles.reduce((s, a) => s + Object.values(a.content).filter(c => c.status === "scheduled").length, 0),
    failed: articles.reduce((s, a) => s + a.failed, 0),
    generated: articles.reduce((s, a) => s + a.readyToPost, 0),
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="w-7 h-7 border-2 border-white/10 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-white">Generated Content Viewer</h1>
        <button onClick={fetchContent} disabled={loading}
          className="inline-flex items-center gap-1.5 bg-white/[0.06] hover:bg-white/[0.09] border border-white/[0.1] text-white/70 rounded-lg px-3.5 py-2 text-sm font-medium transition-colors disabled:opacity-50">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {error && <div className="bg-red/10 border border-red/20 text-red rounded-lg px-4 py-3 text-sm">{error}</div>}
      {success && <div className="bg-green/10 border border-green/20 text-green rounded-lg px-4 py-3 text-sm">{success}</div>}

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          { label: 'Total', value: stats.total, cls: 'text-white' },
          { label: 'Posted', value: stats.posted, cls: 'text-green' },
          { label: 'Scheduled', value: stats.scheduled, cls: 'text-orange' },
          { label: 'Failed', value: stats.failed, cls: 'text-red' },
          { label: 'Ready', value: stats.generated, cls: 'text-primary' },
        ].map((s, i) => (
          <div key={i} className={`${CARD} p-4`}>
            <p className="text-white/40 text-xs mb-1">{s.label}</p>
            <p className={`text-2xl font-bold ${s.cls}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <select className={INPUT} value={platformFilter} onChange={(e) => setPlatformFilter(e.target.value)}>
          <option value="all">All Platforms</option>
          {ALL_PLATFORMS.map(p => (
            <option key={p} value={p}>{PLATFORM_NAMES[p]}</option>
          ))}
        </select>
        <select className={INPUT} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">All Status</option>
          <option value="generated">Generated</option>
          <option value="scheduled">Scheduled</option>
          <option value="posted">Posted</option>
          <option value="failed">Failed</option>
        </select>
      </div>

      {/* Article Cards */}
      {articles.length === 0 ? (
        <div className={`${CARD} flex flex-col items-center justify-center py-16 text-center`}>
          <p className="text-white/40 text-sm">No content found</p>
          <p className="text-white/25 text-xs mt-1">Generated content will appear here once the marketing engine creates posts.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {articles.map((article) => {
            const selectedCount = (selectedPlatforms[article._id] || []).length;

            return (
              <div key={article._id} className={`${CARD} overflow-hidden`}>
                {/* Article header banner */}
                <div
                  className="h-32 flex items-end justify-between p-4 relative"
                  style={{
                    background: article.imageUrl
                      ? `linear-gradient(to bottom, transparent 0%, rgba(17,17,19,0.9) 100%), url(${article.imageUrl}) center/cover`
                      : 'linear-gradient(135deg, rgba(0,122,255,0.3) 0%, rgba(0,81,213,0.3) 100%)',
                  }}
                >
                  <h3 className="text-white font-semibold text-sm leading-snug line-clamp-2 flex-1 min-w-0 pr-3">{article.title}</h3>
                  {article.blogPostSlug && (
                    <a href={`/blog/${article.blogPostSlug}`} target="_blank" rel="noreferrer"
                      className="flex-shrink-0 inline-flex items-center gap-1 bg-white/10 hover:bg-white/20 border border-white/20 text-white/80 hover:text-white rounded-lg px-2.5 py-1 text-xs font-medium transition-colors">
                      <ExternalLink size={11} /> Blog Post
                    </a>
                  )}
                </div>

                <div className="p-4 space-y-4">
                  {/* Status chips */}
                  <div className="flex flex-wrap gap-2">
                    <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-primary/15 text-primary">{article.readyToPost} Ready</span>
                    <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-green/15 text-green">{article.posted} Posted</span>
                    {article.failed > 0 && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-red/15 text-red">{article.failed} Failed</span>
                    )}
                  </div>

                  {/* Platform icons */}
                  <div>
                    <p className="text-white/40 text-xs mb-2 font-medium">Select Platforms to Post:</p>
                    <div className="flex flex-wrap gap-2">
                      {ALL_PLATFORMS.map((platform) => {
                        const content = article.content[platform];
                        const isSelected = (selectedPlatforms[article._id] || []).includes(platform);
                        const hasContent = !!content;
                        const isAvailable = hasContent && content.status === "generated";
                        const color = PLATFORM_COLORS[platform] || '#666';

                        return (
                          <div key={platform} className="relative" title={
                            !hasContent ? `${PLATFORM_NAMES[platform]} — Not generated`
                              : content.status === 'posted' ? `${PLATFORM_NAMES[platform]} — Already posted`
                              : content.status === 'failed' ? `${PLATFORM_NAMES[platform]} — Failed`
                              : isSelected ? `${PLATFORM_NAMES[platform]} — Click to deselect`
                              : `${PLATFORM_NAMES[platform]} — Click to select`
                          }>
                            <button
                              onClick={() => {
                                if (isAvailable) handlePlatformSelect(article._id, platform, !isSelected);
                                else if (hasContent) { setSelectedContent(content); setDialogOpen(true); }
                              }}
                              className="w-10 h-10 rounded-xl flex items-center justify-center transition-all"
                              style={{
                                background: isSelected
                                  ? `${color}30`
                                  : hasContent && content.status === 'posted' ? 'rgba(52,199,89,0.12)'
                                  : hasContent && content.status === 'failed' ? 'rgba(255,59,48,0.12)'
                                  : 'rgba(255,255,255,0.05)',
                                border: isSelected ? `2px solid ${color}` : '2px solid transparent',
                                opacity: isAvailable || hasContent ? 1 : 0.35,
                                cursor: isAvailable ? 'pointer' : hasContent ? 'pointer' : 'not-allowed',
                              }}
                            >
                              <SocialIcon platform={platform} size={20} />
                            </button>

                            {/* Status badge */}
                            {hasContent && content.status === 'posted' && (
                              <div className="absolute -top-1 -right-1 w-4 h-4 bg-green rounded-full flex items-center justify-center">
                                <CheckCircle2 size={10} className="text-white" />
                              </div>
                            )}
                            {hasContent && content.status === 'failed' && (
                              <div className="absolute -top-1 -right-1 w-4 h-4 bg-red rounded-full flex items-center justify-center">
                                <AlertCircle size={10} className="text-white" />
                              </div>
                            )}
                            {hasContent && isAvailable && (
                              <button
                                onClick={(e) => { e.stopPropagation(); setSelectedContent(content); setDialogOpen(true); }}
                                className="absolute -bottom-1 -right-1 w-4 h-4 bg-primary rounded-full flex items-center justify-center hover:bg-primary/80 transition-colors"
                              >
                                <Eye size={9} className="text-white" />
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Post button */}
                  <button
                    onClick={() => handlePostSelected(article._id)}
                    disabled={selectedCount === 0}
                    className="w-full inline-flex items-center justify-center gap-2 bg-green/15 hover:bg-green/25 disabled:bg-white/[0.04] text-green disabled:text-white/30 border border-green/20 disabled:border-white/[0.06] rounded-xl px-4 py-3 text-sm font-semibold transition-colors disabled:cursor-not-allowed"
                  >
                    <Play size={14} />
                    {selectedCount === 0 ? "Select Platforms to Post" : `Post to ${selectedCount} Platform${selectedCount > 1 ? "s" : ""}`}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Content Detail Dialog */}
      <Dialog.Root open={dialogOpen} onOpenChange={(v) => !v && setDialogOpen(false)}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm" />
          <Dialog.Content className="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-[#111113] border border-white/[0.08] rounded-2xl shadow-2xl p-6">
            <div className="flex items-center justify-between mb-5">
              <Dialog.Title className="text-base font-semibold text-white flex items-center gap-2">
                <SocialIcon platform={selectedContent?.platform || ''} size={20} />
                Content — <span className="capitalize">{selectedContent?.platform}</span>
              </Dialog.Title>
              <Dialog.Close className="text-white/40 hover:text-white transition-colors"><X size={18} /></Dialog.Close>
            </div>

            {selectedContent && (
              <div className="space-y-4">
                <div>
                  <p className="text-xs font-semibold text-white/40 mb-1">Article</p>
                  <p className="text-white/80 text-sm font-medium">{selectedContent.article?.title}</p>
                </div>

                <div className="flex items-center justify-between">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${getStatusCls(selectedContent.status)}`}>
                    {selectedContent.status}
                  </span>
                  <span className="text-white/30 text-xs">Created: {new Date(selectedContent.createdAt).toLocaleString()}</span>
                </div>

                <div>
                  <p className="text-xs font-semibold text-white/40 mb-2">Content Text</p>
                  <div className="bg-white/[0.04] border border-white/[0.06] rounded-lg p-4 text-white/70 text-sm whitespace-pre-wrap">
                    {selectedContent.contentText}
                  </div>
                </div>

                {selectedContent.hook && (
                  <div>
                    <p className="text-xs font-semibold text-primary/80 mb-1">Hook</p>
                    <div className="bg-primary/[0.06] border border-primary/20 rounded-lg p-3 text-white/70 text-sm">{selectedContent.hook}</div>
                  </div>
                )}

                {selectedContent.cta && (
                  <div>
                    <p className="text-xs font-semibold text-green/80 mb-1">Call to Action</p>
                    <div className="bg-green/[0.06] border border-green/20 rounded-lg p-3 text-white/70 text-sm">{selectedContent.cta}</div>
                  </div>
                )}

                {selectedContent.videoScript && (
                  <div>
                    <p className="text-xs font-semibold text-orange/80 mb-1">Video Script</p>
                    <div className="bg-orange/[0.06] border border-orange/20 rounded-lg p-3 text-white/70 text-sm whitespace-pre-wrap">
                      {selectedContent.videoScript}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-white/[0.06]">
              <button onClick={() => setDialogOpen(false)}
                className="inline-flex items-center gap-1.5 bg-white/[0.06] hover:bg-white/[0.09] border border-white/[0.1] text-white/70 rounded-lg px-3.5 py-2 text-sm font-medium transition-colors">
                Close
              </button>
              {selectedContent?.status === "generated" && (
                <button
                  onClick={() => { if (selectedContent) { handlePostNow(selectedContent._id); setDialogOpen(false); } }}
                  className="inline-flex items-center gap-1.5 bg-green/15 hover:bg-green/25 text-green border border-green/20 rounded-lg px-3.5 py-2 text-sm font-medium transition-colors">
                  <Play size={14} /> Post Now
                </button>
              )}
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}

export default ContentViewer;
